import { atom } from 'jotai';
import { atomWithObservable, atomFamily } from 'jotai/utils';
import { from } from 'rxjs';
import { TokenService } from '@/services/auth/tokenService';
import { createApiClient } from '@/services/api/httpClient';
import { createLongPollingObservable } from '@/utils/longPolling';
import { EventsResponse, CaseStatus } from '@/types/api/events';
import { selectedPropertiesAtom } from '@/features/property-search/atoms';

const fallbackEventsResponse: EventsResponse = {
  events: [],
  lastEvaluatedKey: '',
  hasMore: false,
};

// Atom to track selected client for filtering events
export const selectedClientForFilteringAtom = atom(async (get) => {
  const selectedProperties = await get(selectedPropertiesAtom);
  // Return the first selected property's ID as the client_id for filtering
  // If multiple properties are selected, we use the first one
  return selectedProperties.length > 0 ? selectedProperties[0].id : null;
});

// Filtered events by case status
export const closedEventsAtom = atom(async () => {
  const tokenService = TokenService.getInstance();
  const authToken = await tokenService.getCurrentToken();

  if (!authToken) {
    throw new Error('Authentication required');
  }

  const apiClient = createApiClient(() => authToken);
  return apiClient.get<EventsResponse>('/events?case_status=closed');
});

export const inProgressEventsAtom = atom(async () => {
  const tokenService = TokenService.getInstance();
  const authToken = await tokenService.getCurrentToken();

  if (!authToken) {
    throw new Error('Authentication required');
  }

  const apiClient = createApiClient(() => authToken);
  return apiClient.get<EventsResponse>('/events?case_status=in_progress');
});

export const openEventsAtom = atom(async () => {
  const tokenService = TokenService.getInstance();
  const authToken = await tokenService.getCurrentToken();

  if (!authToken) {
    throw new Error('Authentication required');
  }

  const apiClient = createApiClient(() => authToken);
  return apiClient.get<EventsResponse>('/events?case_status=open');
});

// Observable atoms for real-time updates
export const closedEventsObservableAtom = atomWithObservable(() => {
  const tokenService = TokenService.getInstance();
  const storedTokens = tokenService.getStoredTokens();

  if (!storedTokens || tokenService.isTokenExpired()) {
    return from(Promise.resolve(fallbackEventsResponse));
  }
  return createLongPollingObservable(
    '/events?case_status=closed',
    storedTokens.idToken,
    5000,
    fallbackEventsResponse,
  );
});

export const inProgressEventsObservableAtom = atomWithObservable(() => {
  const tokenService = TokenService.getInstance();
  const storedTokens = tokenService.getStoredTokens();

  if (!storedTokens || tokenService.isTokenExpired()) {
    return from(Promise.resolve(fallbackEventsResponse));
  }

  return createLongPollingObservable(
    '/events?case_status=in_progress',
    storedTokens.idToken,
    5000,
    fallbackEventsResponse,
  );
});

// WebSocket-based observable for open events (unattended)
// TODO: Replace with actual WebSocket implementation
export const openEventsObservableAtom = atomWithObservable(() => {
  const tokenService = TokenService.getInstance();
  const storedTokens = tokenService.getStoredTokens();

  if (!storedTokens || tokenService.isTokenExpired()) {
    return from(Promise.resolve(fallbackEventsResponse));
  }

  // For now, use long polling until WebSocket is implemented
  return createLongPollingObservable(
    '/events?case_status=open',
    storedTokens.idToken,
    2000,
    fallbackEventsResponse,
  );
});

// Filtered events observable atoms with client_id parameter
export const filteredClosedEventsObservableAtom = atomFamily(
  (clientId?: string) =>
    atomWithObservable(() => {
      const tokenService = TokenService.getInstance();
      const storedTokens = tokenService.getStoredTokens();

      if (!storedTokens || tokenService.isTokenExpired()) {
        return from(Promise.resolve(fallbackEventsResponse));
      }

      const endpoint = clientId
        ? `/events?case_status=closed&client_id=${clientId}`
        : '/events?case_status=closed';
      return createLongPollingObservable(
        endpoint,
        storedTokens.idToken,
        5000,
        fallbackEventsResponse,
      );
    }),
);

export const filteredInProgressEventsObservableAtom = atomFamily(
  (clientId?: string) =>
    atomWithObservable(() => {
      const tokenService = TokenService.getInstance();
      const storedTokens = tokenService.getStoredTokens();

      if (!storedTokens || tokenService.isTokenExpired()) {
        return from(Promise.resolve(fallbackEventsResponse));
      }

      const endpoint = clientId
        ? `/events?case_status=in_progress&client_id=${clientId}`
        : '/events?case_status=in_progress';
      return createLongPollingObservable(
        endpoint,
        storedTokens.idToken,
        5000,
        fallbackEventsResponse,
      );
    }),
);

export const filteredOpenEventsObservableAtom = atomFamily(
  (clientId?: string) =>
    atomWithObservable(() => {
      const tokenService = TokenService.getInstance();
      const storedTokens = tokenService.getStoredTokens();

      if (!storedTokens || tokenService.isTokenExpired()) {
        return from(Promise.resolve(fallbackEventsResponse));
      }

      const endpoint = clientId
        ? `/events?case_status=open&client_id=${clientId}`
        : '/events?case_status=open';
      return createLongPollingObservable(
        endpoint,
        storedTokens.idToken,
        2000,
        fallbackEventsResponse,
      );
    }),
);

// AtomFamily to select the correct observable based on event type
export const eventsObservableAtomFamily = atomFamily(
  (eventType: CaseStatus) => {
    switch (eventType) {
      case 'closed':
        return closedEventsObservableAtom;
      case 'in_progress':
        return inProgressEventsObservableAtom;
      case 'open':
        return openEventsObservableAtom;
      default:
        return atom(() => fallbackEventsResponse);
    }
  },
);

// AtomFamily that combines event type and client filtering
export const filteredEventsObservableAtomFamily = atomFamily(
  (params: { eventType: CaseStatus; clientId?: string }) => {
    const { eventType, clientId } = params;
    switch (eventType) {
      case 'closed':
        return filteredClosedEventsObservableAtom(clientId);
      case 'in_progress':
        return filteredInProgressEventsObservableAtom(clientId);
      case 'open':
        return filteredOpenEventsObservableAtom(clientId);
      default:
        return atom(() => fallbackEventsResponse);
    }
  },
);
